import { type FC, Fragment, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  ContentTypeType,
  PollStatusType,
  PollTriggerType,
  StreamStatusType,
} from "@/types/graphql/partner.generated";
import {
  pollErrorKey,
  useCloseContentPollMutation,
  useContentPollsQuery,
  useDeleteContentPollMutation,
  useOpenContentPollMutation,
  type ContentPoll,
} from "@/partner/entities/polls";
import {
  formatOffset,
  isContentPollEditable,
  POLL_STATUS_LABEL_KEYS,
  POLL_TRIGGER_LABEL_KEYS,
  shouldPollResults,
} from "../model/contentPollForm";
import { AttachPollDialog } from "./AttachPollDialog";
import { PollResults } from "./PollResults";

/** Результаты открытого опроса перезапрашиваются раз в 3 с (подписок у партнёра нет). */
const RESULTS_POLL_INTERVAL_MS = 3000;

interface PollsPanelProps {
  contentId: string;
  contentType: ContentTypeType;
  streamStatus?: StreamStatusType | null;
}

/**
 * Опросы библиотеки, прикреплённые к видео или эфиру (спека, «Переделка:
 * опросы как виджеты»): запуск, статус, ответы, результаты по строке.
 */
export const PollsPanel: FC<PollsPanelProps> = ({
  contentId,
  contentType,
  streamStatus,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // undefined: диалог закрыт, null: добавить новое прикрепление.
  const [dialogPoll, setDialogPoll] = useState<ContentPoll | null | undefined>(
    undefined
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, loading, startPolling, stopPolling } = useContentPollsQuery({
    variables: { contentId },
  });
  const polls = data?.contentPolls ?? [];
  const isStream = contentType === ContentTypeType.StreamType;
  const streamLive = isStream && streamStatus === StreamStatusType.StartedType;

  const shouldPoll = shouldPollResults({
    contentType,
    streamLive,
    statuses: polls.map((poll) => poll.status),
  });
  useEffect(() => {
    if (shouldPoll) startPolling(RESULTS_POLL_INTERVAL_MS);
    else stopPolling();
    return () => stopPolling();
  }, [shouldPoll, startPolling, stopPolling]);

  const onError = (error: Error) =>
    enqueueSnackbar(t(pollErrorKey(error)), { variant: "error" });
  const [openPoll] = useOpenContentPollMutation({ onError });
  const [closePoll] = useCloseContentPollMutation({ onError });
  const [detachPoll] = useDeleteContentPollMutation({
    onError,
    onCompleted: () =>
      enqueueSnackbar(t("polls.snackbar.detached"), { variant: "success" }),
  });

  const handleDetach = (poll: ContentPoll) => {
    if (!window.confirm(t("polls.confirmDetach"))) return;
    void detachPoll({ variables: { id: poll.id } });
  };

  const triggerText = (poll: ContentPoll) => {
    if (
      poll.trigger === PollTriggerType.AtOffsetType &&
      poll.offsetSeconds !== null &&
      poll.offsetSeconds !== undefined
    ) {
      return t("polls.trigger.atOffsetValue", {
        offset: formatOffset(poll.offsetSeconds),
      });
    }
    if (poll.trigger === PollTriggerType.AtTimeType && poll.startsAt) {
      return t("polls.trigger.atTimeValue", { value: new Date(poll.startsAt) });
    }
    return t(POLL_TRIGGER_LABEL_KEYS[poll.trigger]);
  };

  const columns = isStream ? 5 : 4;

  return (
    <>
      <Card elevation={0} sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            p: 2,
          }}
        >
          <Typography variant="subtitle1">{t("polls.title")}</Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={() => setDialogPoll(null)}
          >
            {t("common.actions.add")}
          </Button>
        </Box>
        {loading && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : polls.length === 0 ? (
          <Typography
            color="text.secondary"
            align="center"
            sx={{ py: 6, px: 2 }}
          >
            {t("polls.empty")}
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("polls.columns.question")}</TableCell>
                <TableCell>{t("polls.columns.trigger")}</TableCell>
                {isStream && <TableCell>{t("polls.columns.status")}</TableCell>}
                <TableCell align="right">{t("polls.columns.voters")}</TableCell>
                <TableCell align="right">
                  {t("polls.columns.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {polls.map((poll) => (
                <Fragment key={poll.id}>
                  <TableRow
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      setExpandedId((prev) =>
                        prev === poll.id ? null : poll.id
                      )
                    }
                  >
                    <TableCell>{poll.question}</TableCell>
                    <TableCell>{triggerText(poll)}</TableCell>
                    {isStream && (
                      <TableCell>
                        {poll.status
                          ? t(POLL_STATUS_LABEL_KEYS[poll.status])
                          : null}
                      </TableCell>
                    )}
                    <TableCell align="right">{poll.votersCount}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: "nowrap" }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {streamLive &&
                        (poll.status === PollStatusType.DraftType ||
                          poll.status === PollStatusType.ScheduledType) && (
                          <Button
                            size="small"
                            onClick={() =>
                              void openPoll({ variables: { id: poll.id } })
                            }
                          >
                            {t("polls.actions.open")}
                          </Button>
                        )}
                      {isStream &&
                        poll.status === PollStatusType.ActiveType && (
                          <Button
                            size="small"
                            onClick={() =>
                              void closePoll({ variables: { id: poll.id } })
                            }
                          >
                            {t("polls.actions.close")}
                          </Button>
                        )}
                      {isContentPollEditable(poll, contentType) && (
                        <Tooltip title={t("common.actions.edit")}>
                          <IconButton onClick={() => setDialogPoll(poll)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t("common.actions.delete")}>
                        <IconButton onClick={() => handleDetach(poll)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={columns}
                      sx={{
                        p: 0,
                        borderBottom:
                          expandedId === poll.id ? undefined : "none",
                      }}
                    >
                      <Collapse in={expandedId === poll.id} unmountOnExit>
                        <PollResults poll={poll} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      {dialogPoll !== undefined && (
        <AttachPollDialog
          contentId={contentId}
          contentType={contentType}
          contentPoll={dialogPoll}
          onClose={() => setDialogPoll(undefined)}
        />
      )}
    </>
  );
};
